import { Group, Modal, MultiSelect, SelectItem, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

export interface SyncRemoteDeviceModalProps {
  remoteSourceId: number;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for syncing a remote device.
 */
export default function SyncRemoteDeviceModal({ remoteSourceId, opened, onClose }: SyncRemoteDeviceModalProps) {
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [externalIds, setExternalIds] = useState<SelectItem[]>([]);
  const syncDeviceAction = useMemo(() => getAction('RemoteSourceSyncDevice'), [getAction]);
  const listDevicesAction = useMemo(() => getAction('DeviceList'), [getAction]);

  const form = useForm({
    initialValues: {
      externalIds: [] as string[],
    },
    validate: {
      externalIds: (value) => (value.length === 0 ? 'Vyplňte aspoň jedno externé ID' : null),
    },
  });

  /** Sync devices with the provided external IDs. */
  const syncDevices = async ({ externalIds }: typeof form.values) => {
    try {
      startLoading();

      for (const externalId of externalIds) {
        await syncDeviceAction({ parameters: { remoteSourceId: String(remoteSourceId), deviceRemoteId: externalId } });
        setExternalIds((current) =>
          current.map((item) => (item.value === externalId ? { ...item, label: `✅ ${item.label}` } : item))
        );
      }

      showNotification({
        title: 'Synchronizácia dokončená',
        message: 'Zariadenia boli úspešne synchronizované.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      onClose();
    } catch (err: any) {
      panic(err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    listDevicesAction({ query: { filters: { 'remoteSourceId.eq': remoteSourceId } } })
      .then((devices) =>
        setExternalIds(
          devices
            .filter(({ externalId }) => !!externalId)
            .map(({ externalId, deviceName }) => ({ value: externalId!, label: `${deviceName} (#${externalId})` }))
        )
      )
      .catch(panic);
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Synchronizácia zariadení"
      size="md"
      centered
      closeButtonProps={{ loading }}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <form onSubmit={form.onSubmit(syncDevices)}>
        <Stack spacing={24}>
          <MultiSelect
            data={externalIds}
            getCreateLabel={(query) => `+ Vytvoriť nové externé ID: ${query}`}
            onCreate={(query) => {
              setExternalIds((current) => [...current, { value: query, label: `Nové (#${query})` }]);
              return query;
            }}
            creatable
            searchable
            readOnly={loading}
            label="Externé ID"
            placeholder="Vyberte zariadenia alebo vytvorte nové externé ID"
            {...form.getInputProps('externalIds')}
          />
          <Group position="apart" noWrap display={loading ? 'none' : undefined}>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => form.setValues({ externalIds: externalIds.map((item) => item.value) })}
            >
              Vybrať všetky
            </Button>
            <Group position="right" noWrap>
              <Button variant="subtle-gray" onClick={onClose} type="button">
                Zrušiť
              </Button>
              <Button variant="primary" type="submit">
                Synchronizovať
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
